import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../lib/googleMaps';

const ADDRESS_TYPES = new Set([
  'street_address', 'route', 'intersection', 'premise', 'subpremise', 'postal_code'
]);

export default function BusinessSearch({ onSelect }) {
  const containerRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let active = true;
    let widget;

    async function setup() {
      try {
        const maps = await loadGoogleMaps();
        const { PlaceAutocompleteElement } = await maps.importLibrary('places');
        if (!active) return;

        widget = new PlaceAutocompleteElement({ includedRegionCodes: ['us'] });
        widget.setAttribute('aria-label', 'Search for a local Google business');
        widget.style.display = 'block';
        widget.style.width = '100%';
        widget.addEventListener('gmp-select', async (event) => {
          setError('');
          onSelectRef.current(null);
          try {
            const place = event.placePrediction.toPlace();
            await place.fetchFields({
              fields: ['id', 'displayName', 'formattedAddress', 'location', 'googleMapsURI', 'types', 'photos']
            });
            if (!active) return;
            if (!place.id || !place.location || !place.displayName || place.types?.some((type) => ADDRESS_TYPES.has(type))) {
              setError('Please choose a business rather than a street address.');
              return;
            }
            onSelectRef.current({
              placeId: place.id,
              name: place.displayName,
              address: place.formattedAddress,
              location: place.location,
              mapsUrl: place.googleMapsURI,
              imageUrl: place.photos?.[0]?.getURI({ maxWidth: 1200, maxHeight: 800 }) || ''
            });
          } catch {
            if (active) setError('We could not load that business. Please try another result.');
          }
        });
        containerRef.current?.appendChild(widget);
        setLoading(false);
      } catch {
        if (active) {
          setLoading(false);
          setError('Business search is unavailable right now. Check that Maps JavaScript API and Places API (New) are enabled for this site.');
        }
      }
    }

    setup();
    return () => {
      active = false;
      widget?.remove();
    };
  }, []);

  return (
    <div>
      <div ref={containerRef} className="min-h-[56px] rounded-xl bg-white text-[#0b1423]">
        {loading && <p className="px-5 py-4 text-sm text-slate-500">Loading business search…</p>}
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-[#f8b86a]">{error}</p>}
    </div>
  );
}